// DM roll requests: an ability check (plain, skill or tool) or saving throw the DM asks of characters, with a DC. A
// check can be a group check. Another character with Boon of Fate can change a roll with Improve Fate while the request
// is open. Pure; shared by the DM page, the sheet and the server. The skill keys and abilities match the sheet's skill
// schema.
export const ROLL_REQUEST_ABILITIES = Object.freeze(['str', 'dex', 'con', 'int', 'wis', 'cha']);
export const ABILITY_NAMES = Object.freeze({ str: 'Strength', dex: 'Dexterity', con: 'Constitution', int: 'Intelligence', wis: 'Wisdom', cha: 'Charisma' });
export const SAVE_ABILITY = Object.freeze({ str: 'strength', dex: 'dexterity', con: 'constitution', int: 'intelligence', wis: 'wisdom', cha: 'charisma' });
export const SKILLS = Object.freeze({
  acrobatics: { name: 'Acrobatics', ability: 'dex' }, animalHandling: { name: 'Animal Handling', ability: 'wis' },
  arcana: { name: 'Arcana', ability: 'int' }, athletics: { name: 'Athletics', ability: 'str' },
  deception: { name: 'Deception', ability: 'cha' }, history: { name: 'History', ability: 'int' },
  insight: { name: 'Insight', ability: 'wis' }, intimidation: { name: 'Intimidation', ability: 'cha' },
  investigation: { name: 'Investigation', ability: 'int' }, medicine: { name: 'Medicine', ability: 'wis' },
  nature: { name: 'Nature', ability: 'int' }, perception: { name: 'Perception', ability: 'wis' },
  performance: { name: 'Performance', ability: 'cha' }, persuasion: { name: 'Persuasion', ability: 'cha' },
  religion: { name: 'Religion', ability: 'int' }, sleightOfHand: { name: 'Sleight of Hand', ability: 'dex' },
  stealth: { name: 'Stealth', ability: 'dex' }, survival: { name: 'Survival', ability: 'wis' },
});
export const MAX_ROLL_REQUEST_TARGETS = 12;
/** Improve Fate reaches a creature within 60 feet of the boon holder. */
export const FATE_HELP_RANGE_FEET = 60;

/** What the request asks for, e.g. "Strength (Athletics) check" or "Wisdom saving throw". */
export const rollRequestLabel = ({ kind, ability, skill, tool } = {}) => {
  const name = ABILITY_NAMES[ability] || 'Ability';
  if (kind === 'save') return `${name} saving throw`;
  if (skill && SKILLS[skill]) return `${name} (${SKILLS[skill].name}) check`;
  if (tool) return `${name} check with ${tool}`;
  return `${name} check`;
};

/** The request's spec, normalized, or an error message. */
export function normalizeRollRequestSpec(input = {}) {
  const kind = input.kind === 'save' ? 'save' : input.kind === 'check' ? 'check' : null;
  if (!kind) return { error: 'Choose a check or a saving throw.' };
  const skill = kind === 'check' && input.skill ? String(input.skill) : null;
  if (skill && !SKILLS[skill]) return { error: 'Choose a skill from the list.' };
  const tool = kind === 'check' && !skill && typeof input.tool === 'string' && input.tool.trim() ? input.tool.trim().slice(0, 60) : null;
  const ability = ROLL_REQUEST_ABILITIES.includes(input.ability) ? input.ability : skill ? SKILLS[skill].ability : null;
  if (!ability) return { error: 'Choose an ability.' };
  const dc = Number(input.dc);
  if (!Number.isInteger(dc) || dc < 1 || dc > 40) return { error: 'The DC is a whole number from 1 to 40.' };
  const forcedMode = ['advantage', 'disadvantage'].includes(input.forcedMode) ? input.forcedMode : null;
  const reason = typeof input.reason === 'string' ? input.reason.trim().slice(0, 140) : '';
  const group = kind === 'check' && input.group === true;
  // The DM's mark that a failed save applies Charmed or Frightened, so Countercharm can answer it.
  const charmOrFear = kind === 'save' && input.charmOrFear === true;
  return { spec: { kind, ability, ...(skill ? { skill } : {}), ...(tool ? { tool } : {}), dc, hiddenDc: input.hiddenDc === true, ...(forcedMode ? { forcedMode } : {}),
    ...(reason ? { reason } : {}), ...(group ? { group: true } : {}), ...(charmOrFear ? { charmOrFear: true } : {}) } };
}

/**
 * A finished roll's log line: the d20 used (both dice with Advantage or Disadvantage, the first die when a rescue
 * changed it), the bonus, any rescue, the total against the DC (when the viewer knows it) and the outcome.
 */
export function rollRequestLogLine(spec, target, result) {
  const dc = Number.isFinite(spec?.dc) ? ` vs DC ${spec.dc}` : '';
  if (result.automaticFailure) return `Automatic failure${dc}`;
  const both = Array.isArray(result.dice) && result.dice.length === 2 ? ` (${result.mode === 'disadvantage' ? 'Disadvantage' : 'Advantage'}: ${result.dice.join(', ')})` : '';
  const first = Number.isInteger(result.naturalD20) ? `, first ${result.naturalD20}` : '';
  const rescue = result.strokeOfLuck ? ' · Stroke of Luck' : result.indomitable ? ` · Indomitable +${result.indomitable.bonus}`
    : result.heroicInspiration ? ' · Heroic Inspiration' : result.fate ? ` · Boon of Fate +${result.fate.bonus}` : result.bardicInspiration ? ` · Bardic Inspiration +${result.bardicInspiration.bonus}` : result.peerlessSkill ? ` · Peerless Skill +${result.peerlessSkill.bonus}` : result.countercharm ? ` · Countercharm (${result.countercharm.by})` : '';
  const bonus = Number(target?.bonus) || 0;
  return `d20 ${result.d20}${both}${first} · bonus ${bonus >= 0 ? '+' : '−'}${Math.abs(bonus)}${rescue}${fateHelpNote(result)} · total ${result.total}${dc} · ${result.success ? 'Success' : 'Failure'}`;
}

/** Another character's Improve Fate on the roll, e.g. " · Improve Fate (Elara) +5". */
export const fateHelpNote = result => (result?.helped
  ? ` · Improve Fate (${result.helped.name}) ${result.helped.sign < 0 ? '−' : '+'}${result.helped.bonus}` : '');

/**
 * A group check (2024): the group succeeds if at least half its members succeed. `success` is null until everyone has
 * rolled.
 */
export function groupCheckOutcome(targets = []) {
  const rolled = targets.filter(target => target.result);
  const successes = rolled.filter(target => target.result.success).length;
  return { size: targets.length, rolled: rolled.length, successes,
    success: targets.length > 0 && rolled.length === targets.length ? successes * 2 >= targets.length : null };
}

export const groupCheckLine = (group) => (group.success === null ? `Group check: ${group.rolled} of ${group.size} rolled`
  : `Group check: ${group.successes} of ${group.size} succeeded · ${group.success ? 'Success' : 'Failure'}`);
